// KARS-KI: Antworttext → Blockmodell.
//
// Das Modell antwortet in schlichtem Markdown (Absätze, Listen, **fett**,
// `code`) und setzt Direktlinks als Token `[[route:PageX?k=v|Beschriftung]]`.
// Dieser Parser erzeugt ausschließlich strukturierte Daten; gerendert wird
// mit Vue-Komponenten (nie innerHTML), damit kein Modelltext als HTML
// interpretiert werden kann. Bewusst ohne Vue-Abhängigkeit (Vitest/Node).

export type InlineRun
  = | { kind: 'text', text: string }
    | { kind: 'bold', text: string }
    | { kind: 'code', text: string }
    | { kind: 'route', target: string, label: string }

export type Block
  = | { kind: 'paragraph', runs: InlineRun[] }
    | { kind: 'heading', runs: InlineRun[] }
    | { kind: 'list', ordered: boolean, items: InlineRun[][] }

/** Zeile mit Anschlussfragen am Ende der Antwort (siehe Prompt-Regeln). */
const SUGGESTIONS_LINE = /^\s*(?:\*\*)?VORSCHL(?:Ä|AE|A)GE:?(?:\*\*)?:?\s*(.*)$/im

export interface ParsedAnswer {
  /** Antworttext ohne die Vorschlagszeile. */
  text: string
  suggestions: string[]
}

/** Trennt die Vorschlagszeile vom Antworttext. */
export function splitSuggestions (raw: string): ParsedAnswer {
  const match = SUGGESTIONS_LINE.exec(raw)
  if (!match) {
    return { text: raw.trim(), suggestions: [] }
  }
  const text = (raw.slice(0, match.index) + raw.slice(match.index + match[0].length)).trim()
  const suggestions = (match[1] ?? '')
    .split(/\s*\|\s*|\s*;\s*|\s+•\s+/)
    .map(s => s.replace(/^[-*\d.)\s]+/, '').trim())
    .filter(Boolean)
    .slice(0, 3)
  return { text, suggestions }
}

/**
 * Text, der während des Streamings sicher angezeigt werden kann: ein noch
 * offenes Link-Token am Ende und eine angefangene Vorschlagszeile bleiben
 * zurückgehalten, bis sie vollständig sind.
 */
export function streamingSafeText (raw: string): string {
  let text = raw
  const openToken = text.lastIndexOf('[[')
  if (openToken !== -1 && !text.includes(']]', openToken)) {
    text = text.slice(0, openToken)
  }
  const lastBreak = text.lastIndexOf('\n')
  const lastLine = text.slice(lastBreak + 1)
  if (/^\s*(?:\*\*)?V(?:O(?:R(?:S(?:C(?:H(?:L(?:[ÄA](?:E?G?E?:?.*)?)?)?)?)?)?)?)?$/i.test(lastLine) && lastLine.trim().length > 0) {
    text = text.slice(0, Math.max(lastBreak, 0))
  }
  return text
}

// Fett: **…** oder __…__, auch mit Satzzeichen oder einzelnen Sternen innen;
// *kursiv* wird als normaler Text übernommen. Übrig gebliebene Marker
// entfernt stripStrayMarkers.
const INLINE = /\[\[route:([^\]|]+)\|([^\]]+)\]\]|\*\*([^\n]+?)\*\*|__([^\n]+?)__|`([^`\n]+)`|\[([^\]]+)\]\((?:[^)\s]+)\)|(?<![\w*])\*([^*\n]+?)\*(?![\w*])/g

/** Zerlegt eine Zeile in Text-, Fett-, Code- und Link-Läufe. */
export function parseInline (line: string): InlineRun[] {
  const runs: InlineRun[] = []
  let last = 0
  for (const m of line.matchAll(INLINE)) {
    const index = m.index ?? 0
    if (index > last) {
      runs.push({ kind: 'text', text: line.slice(last, index) })
    }
    if (m[1] !== undefined) {
      runs.push({ kind: 'route', target: m[1].trim(), label: (m[2] ?? '').trim() })
    } else if (m[3] !== undefined) {
      runs.push({ kind: 'bold', text: stripStrayMarkers(m[3]) })
    } else if (m[4] !== undefined) {
      runs.push({ kind: 'bold', text: stripStrayMarkers(m[4]) })
    } else if (m[5] !== undefined) {
      runs.push({ kind: 'code', text: m[5] })
    } else if (m[6] !== undefined) {
      runs.push({ kind: 'text', text: m[6] })
    } else if (m[7] !== undefined) {
      runs.push({ kind: 'text', text: m[7] })
    }
    last = index + m[0].length
  }
  if (last < line.length) {
    runs.push({ kind: 'text', text: line.slice(last) })
  }
  return mergeText(runs.map(run => (run.kind === 'text' ? { kind: 'text', text: stripStrayMarkers(run.text) } : run)))
}

/** Entfernt alleinstehende Fett-Marker, die das Modell nicht geschlossen hat. */
export function stripStrayMarkers (text: string): string {
  return text.replace(/\*\*|__/g, '')
}

function mergeText (runs: InlineRun[]): InlineRun[] {
  return runs.reduce<InlineRun[]>((acc, run) => {
    // Array#at fehlt in der TS-Lib-Konfiguration (vor ES2022).
    // eslint-disable-next-line unicorn/prefer-at
    const prev = acc[acc.length - 1]
    if (prev && prev.kind === 'text' && run.kind === 'text') {
      acc[acc.length - 1] = { kind: 'text', text: prev.text + run.text }
    } else {
      acc.push(run)
    }
    return acc
  }, [])
}

const ORDERED = /^\s*(\d+)[.)]\s+(.*)$/
const UNORDERED = /^\s*[-*•]\s+(.*)$/
const HEADING = /^\s*#{1,4}\s+(.*)$/

/** Markdown-Text → Blöcke. Leere Zeilen trennen Absätze. */
export function parseBlocks (text: string): Block[] {
  const blocks: Block[] = []
  let paragraph: string[] = []
  let list: { ordered: boolean, items: InlineRun[][] } | null = null

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', runs: parseInline(paragraph.join(' ')) })
      paragraph = []
    }
  }
  const flushList = () => {
    if (list) {
      blocks.push({ kind: 'list', ordered: list.ordered, items: list.items })
      list = null
    }
  }

  for (const rawLine of text.replace(/\r\n/g, '\n').split('\n')) {
    const line = rawLine.trimEnd()
    if (!line.trim()) {
      flushParagraph()
      flushList()
      continue
    }
    const heading = HEADING.exec(line)
    if (heading) {
      flushParagraph()
      flushList()
      blocks.push({ kind: 'heading', runs: parseInline(heading[1] ?? '') })
      continue
    }
    const ordered = ORDERED.exec(line)
    const unordered = ordered ? null : UNORDERED.exec(line)
    if (ordered || unordered) {
      flushParagraph()
      const isOrdered = Boolean(ordered)
      if (!list || list.ordered !== isOrdered) {
        flushList()
        list = { ordered: isOrdered, items: [] }
      }
      list.items.push(parseInline((ordered ? ordered[2] : unordered?.[1]) ?? ''))
      continue
    }
    if (list && /^\s{2,}/.test(rawLine)) {
      // Eingerückte Fortsetzungszeile gehört zum letzten Listenpunkt.
      // eslint-disable-next-line unicorn/prefer-at
      const lastItem = list.items[list.items.length - 1]
      if (lastItem) {
        lastItem.push({ kind: 'text', text: ' ' }, ...parseInline(line.trim()))
        continue
      }
    }
    flushList()
    paragraph.push(line.trim())
  }
  flushParagraph()
  flushList()
  return blocks
}

/** Reiner Text einer Antwort (z. B. für die Zwischenablage oder Tests). */
export function plainText (blocks: Block[]): string {
  const runText = (runs: InlineRun[]) => runs.map(r => (r.kind === 'route' ? r.label : r.text)).join('')
  return blocks
    .map(b => (b.kind === 'list' ? b.items.map((item, i) => `${b.ordered ? `${i + 1}.` : '•'} ${runText(item)}`).join('\n') : runText(b.runs)))
    .join('\n\n')
}
